// views/payments/BillPaymentView.tsx
import React, {useEffect, useState} from 'react';
import {Button, Form, Modal, Table} from 'antd';
import {useTheme} from "@/components/common/ThemeProvider";
import {
    useActivatePhoneNumber,
    useGetBills,
    useGetLinkedNumbers,
    useLinkPhoneNumber,
    useTriggerPushPayment
} from '@/hooks/payments/payment';
import {CustomerBillDTO} from '@/shared/api/model';
import {formatAmount} from "@/components/common/utils/money";
import {useAuthUser} from "@/modules/auth/context/AuthContext";
import {useGetFspsByType} from "@/modules/admin/fsps/hooks/fsp";
import {useGetPaymentProcess} from "@/modules/admin/fsps/hooks/use-payment-process";
import {PaymentForm} from "@/views/payments/PaymentForm";
import {PaymentStatusModal} from "@/views/payments/PaymentStatusModal";
import {BankProcessModal} from "@/views/payments/BankProcessModal";
import {useGetTransactionStatus} from "@/hooks/invitations/invitations";
import {
    showErrorNotification,
    showSuccessNotification,
    showWarningNotification
} from "@/components/common/utils/notify";
import {
    FspType, ProcessCodes, PushResponseDTO,
    ScaleTransactionStatus,
    TransactionPriceResultDTO,
    TransactionRecordDTO, TransactionTypes
} from "@/shared/api/model";
import {useGetTransactionPrice} from "@/shared/api/client/business-controller";
import { PriceConfirmationModal } from './PriceConfirmationModal';


const BillPaymentView: React.FC = () => {
    const { dark } = useTheme();
    const { fullname } = useAuthUser();
    const [form] = Form.useForm();


    // States
    const [currentFspType, setCurrentFspType] = useState<FspType>(FspType.MNO);
    const [selectedFsp, setSelectedFsp] = useState<string>('');
    const [selectedBill, setSelectedBill] = useState<CustomerBillDTO | null>(null);
    const [amount, setAmount] = useState<string>();
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [isLinkedNumber, setIsLinkedNumber] = useState(false);
    const [showOtpInput, setShowOtpInput] = useState(false);

    // Modal states
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isPaymentStatusOpen, setIsPaymentStatusOpen] = useState(false);
    const [isBankProcessOpen, setIsBankProcessOpen] = useState(false);

    // Payment status states
    const [isPolling, setIsPolling] = useState(false);
    const [pollingAttempts, setPollingAttempts] = useState(0);
    const [transactionRequestId, setTransactionRequestId] = useState<string | null>(null);

    // Queries and mutations
    const [{ data: bills, isLoading: isBillsLoading }, refetchBills] = useGetBills();
    const [{ data: fsps, isLoading: isLoadingFsps }] = useGetFspsByType(currentFspType);
    const [{ data: linkedNumbers }] = useGetLinkedNumbers();
    const [{ data: paymentProcess }] = useGetPaymentProcess(currentFspType,selectedFsp, 'en');
    const { mutateAsync: triggerPushPayment, isPending: isPushLoading } = useTriggerPushPayment();
    const { mutateAsync: linkPhoneNumber, isPending: isLinkingPhone } = useLinkPhoneNumber();
    const { mutateAsync: activatePhoneNumber, isPending: isActivatingPhone } = useActivatePhoneNumber();
    const { mutateAsync: getTransactionStatus } = useGetTransactionStatus();
    const [isPriceConfirmationOpen, setIsPriceConfirmationOpen] = useState(false);
    const [priceResult, setPriceResult] = useState<TransactionPriceResultDTO | undefined>();
    const { mutateAsync: getTransactionPrice, isPending: isPriceLoading } = useGetTransactionPrice();



    // Effects for transaction status polling
    useEffect(() => {
        let pollingInterval: NodeJS.Timeout;

        if (isPolling && transactionRequestId) {
            pollingInterval = setInterval(() => {
                checkTransactionStatus(transactionRequestId);
            }, 8000);
        }

        return () => {
            if (pollingInterval) clearInterval(pollingInterval);
        };
    }, [isPolling, transactionRequestId]);

    const checkTransactionStatus = async (requestId: string) => {
        try {
            const result:TransactionRecordDTO = await getTransactionStatus(requestId);
            handleTransactionStatus(result.trxStatus);
        } catch (error) {
            console.error('Error checking transaction status:', error);
            showErrorNotification("Transaction status:", 'Failed to check transaction status',dark);
            stopPaymentPolling();
        }
    };

    const handleTransactionStatus = (status?: ScaleTransactionStatus) => {
        if(!status) return;
        switch (status) {
            case ScaleTransactionStatus.PENDING:
                if (pollingAttempts >= 5) {
                    stopPaymentPolling();
                    showWarningNotification('Warning','Transaction is taking longer than expected. Please check your account status.');
                } else {
                    setPollingAttempts(prev => prev + 1);
                }
                break;
            case ScaleTransactionStatus.CONFIRMED:
                stopPaymentPolling();
                showSuccessNotification('Success','Payment confirmed successfully!',dark);
                refetchBills();
                break;
            case ScaleTransactionStatus.FAILED:
                stopPaymentPolling();
                showErrorNotification('Error','Payment failed. Please try again.',dark);
                break;
            default:
                stopPaymentPolling();
                showWarningNotification('Warning','Unexpected transaction status. Please check your account status.');
        }
    };

    const handlePaymentSubmit = async (values: any) => {
        if (!selectedBill || !amount) {
            showErrorNotification('Error', 'Please select a bill and enter amount', dark);
            return;
        }

        try {
            const priceResult = await getTransactionPrice({
                data: {
                    amount: Number(amount),
                    customerId:'NA',
                    transactionType: selectedBill.productType === 'L' ?
                        'LOAN_REPAYMENT' : 'AMOUNT_DEPOSIT'
                }
            });

            setPriceResult(priceResult);
            setIsPriceConfirmationOpen(true);
        } catch (error) {
            showErrorNotification('Error', 'Failed to get transaction pricing', dark);
        }
    };

// Add handlePriceConfirm function
    const handlePriceConfirm = async () => {
        await handleMobilePayment(form.getFieldsValue());
        setIsPriceConfirmationOpen(false);
    };

    const stopPaymentPolling = () => {
        setIsPolling(false);
        setIsPaymentStatusOpen(false);
    };

    const handlePaymentClick = (bill: CustomerBillDTO) => {
        setSelectedBill(bill);
        form.resetFields();
        setCurrentFspType(FspType.MNO);
        setIsPaymentModalOpen(true);
    };

    const handlePhoneNumberChange = (value: string) => {
        setPhoneNumber(value);
        setIsLinkedNumber(linkedNumbers?.some(number => number.phoneNumber === value) ?? false);
    };

    const handleFspTypeChange = (type: FspType) => {
        setCurrentFspType(type);
        setSelectedFsp('');
        form.setFieldsValue({ channelId: undefined });
        setShowOtpInput(false);
    };

    const handleFspChange = (value: string) => {
        setSelectedFsp(value);
    };


    const handleMobilePayment = async (values: any) => {
        console.log(values);
        if (currentFspType === FspType.MNO) {
            // First handle phone number linking if needed
            if (!isLinkedNumber && !showOtpInput) {
                try {
                    await linkPhoneNumber({
                        phoneNumber: values.phoneNumber,
                        channelId: values.channelId,
                    });
                    setShowOtpInput(true);
                    return;
                } catch (error) {
                    showErrorNotification('Error', 'Failed to initiate phone number linking', dark);
                    return;
                }
            }

            // Handle OTP verification if needed
            if (showOtpInput) {
                try {
                    await activatePhoneNumber({
                        phoneNumber: values.phoneNumber,
                        channelId: values.channelId,
                        authorizationToken: values.otp,
                    });
                    setIsLinkedNumber(true);
                    setShowOtpInput(false);
                } catch (error) {
                    showErrorNotification('Error', 'Failed to verify OTP', dark);
                    return;
                }
            }
        }

        if (!priceResult || !priceResult.finalAmount) return;
        const amount = priceResult.finalAmount.toString();

        // Proceed with payment for both bank and mobile money
        try {
            const pushRequest = {
                amount,
                fees: priceResult?.fees,
                fspType:currentFspType,
                fspCode: currentFspType === FspType.MNO ? values.channelId : selectedFsp,
                targetAddress: values.phoneNumber,
                paymentReference: selectedBill?.paymentReference,
                initiationChannel: "CUSTOMER_PORTAL",
                transactionType: TransactionTypes.C2B,
                paymentDescription: `Payment for ${fullname} From Customer Portal`,
                processingCode: selectedBill?.productType === 'L' ?
                    ProcessCodes.LOAN_REPAYMENT : ProcessCodes.AMOUNT_DEPOSIT
            };

            const response: PushResponseDTO = await triggerPushPayment(pushRequest);
            console.log(response);

            if (response.requestId) {
                setTransactionRequestId(response.requestId);

                if (currentFspType === FspType.MNO) {
                    setIsPolling(true);
                    setPollingAttempts(0);
                    setIsPaymentStatusOpen(true);
                    setIsPaymentModalOpen(false);
                } else {
                    // For bank payments, show the bank process modal
                    setIsBankProcessOpen(true);
                    setIsPaymentModalOpen(false);
                }
            }
        } catch (error) {
            console.error(error);
            showErrorNotification('Error', 'Failed to initiate payment', dark);
        }
    };

    const columns = [
        {
            title: 'Product Name',
            dataIndex: 'productName',
            key: 'productName'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (text: string) => <span>{formatAmount(Number(text))}</span>
        },
        {
            title: 'Balance Amount',
            dataIndex: 'balanceAmount',
            key: 'balanceAmount',
            render: (text: string) => <span>{formatAmount(Number(text))}</span>
        },
        {
            title: 'Payment Reference',
            dataIndex: 'paymentReference',
            key: 'paymentReference'
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: CustomerBillDTO) => (
                <Button onClick={() => handlePaymentClick(record)}>Pay</Button>
            ),
        },
    ];

    return (
        <div className={dark ? 'dark-mode' : ''}>
            <h1>Bills</h1>

            <Table
                // @ts-ignore
                dataSource={bills}
                columns={columns}
                loading={isBillsLoading}
                rowKey="paymentReference"
            />

            <Modal
                title="Make Payment"
                open={isPaymentModalOpen}
                onCancel={() => {
                    setIsPaymentModalOpen(false);
                    setShowOtpInput(false);
                    form.resetFields();
                }}
                footer={null}
            >
                <PaymentForm
                    form={form}
                    currentFspType={currentFspType}
                    onFspTypeChange={handleFspTypeChange}
                    onFspChange={handleFspChange}
                    fsps={fsps}
                    isLoadingFsps={isLoadingFsps}
                    onPhoneNumberChange={handlePhoneNumberChange}
                    showOtpInput={showOtpInput}
                    isLinkedNumber={isLinkedNumber}
                    onAmountChange={setAmount}
                    loading={isPushLoading || isLinkingPhone || isActivatingPhone || isPriceLoading }
                    dark={dark}
                    onSubmit={handlePaymentSubmit}
                    // @ts-ignore
                    selectedBill={selectedBill}
                />
            </Modal>

            {/* Payment Status Modal for MNO payments */}
            <PaymentStatusModal
                open={isPaymentStatusOpen}
            />

            {/* Bank Process Modal */}
            <BankProcessModal
                open={isBankProcessOpen}
                onClose={() => setIsBankProcessOpen(false)}
                processDefinition={paymentProcess?.processDefinition}
                amount={priceResult?.finalAmount?.toString()}
                paymentReference={selectedBill?.paymentReference}
            />

            <PriceConfirmationModal
                open={isPriceConfirmationOpen}
                onClose={() => setIsPriceConfirmationOpen(false)}
                onConfirm={handlePriceConfirm}
                priceResult={priceResult}
                loading={isPushLoading || isLinkingPhone || isActivatingPhone}
                dark={dark}
            />
        </div>
    );
};

export default BillPaymentView;